import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, LineChart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="col-span-12 md:col-span-8 flex flex-col justify-center"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading font-black text-lg text-muted-foreground">Real<span className="text-primary">Flation</span></span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
            Track Your <span className="text-primary">Real</span> Inflation
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
            Stop trusting manipulated government statistics. Track your actual purchasing power by monitoring the prices of items you buy regularly - from eggs to rent.
          </p>
          <div className="flex gap-4">
            <Button 
              data-testid="get-started-btn"
              onClick={handleLogin}
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white rounded-full font-bold px-8 transition-transform hover:scale-105 active:scale-95"
            >
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => window.location.href = '/learn-more'}
              variant="outline" 
              size="lg" 
              className="rounded-full font-bold px-8 border-card-border hover:border-primary/50"
            >
              Learn More
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="col-span-12 md:col-span-4"
        >
          <Card className="bg-card border-card-border hover:border-primary/50 transition-colors duration-300">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-primary/20 p-3 rounded-full">
                  <TrendingUp className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">+12.4%</p>
                  <p className="text-sm text-muted-foreground">Your Real Inflation</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <LineChart className="w-8 h-8 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">3.2%</p>
                  <p className="text-sm text-muted-foreground">Official Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-24">
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-center mb-16">
          How It Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<DollarSign className="w-12 h-12" />}
            title="Connect Your Accounts"
            description="Link your credit cards and bank accounts to automatically track your spending on recurring items."
            delay={0}
          />
          <FeatureCard
            icon={<LineChart className="w-12 h-12" />}
            title="Track Price Changes"
            description="We monitor how prices change for the same items you buy - eggs, pasta, rent, and everything else."
            delay={0.2}
          />
          <FeatureCard
            icon={<TrendingUp className="w-12 h-12" />}
            title="See Your Real Rate"
            description="Compare your personal inflation rate against the official numbers. Discover the truth about your purchasing power."
            delay={0.4}
          />
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-24">
        <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
          <CardContent className="p-12 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">
              Ready to Discover Your Real Inflation?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands tracking their true purchasing power
            </p>
            <Button 
              data-testid="cta-get-started-btn"
              onClick={handleLogin}
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white rounded-full font-bold px-8 transition-transform hover:scale-105 active:scale-95"
            >
              Start Tracking Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
    >
      <Card className="bg-card border-card-border hover:border-primary/50 transition-colors duration-300 h-full">
        <CardContent className="p-8">
          <div className="bg-primary/20 p-4 rounded-full w-fit mb-6 text-primary">
            {icon}
          </div>
          <h3 className="font-heading font-bold text-xl mb-4">{title}</h3>
          <p className="text-muted-foreground">{description}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default LandingPage;
